import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import useDarkMode from "../../../hooks/useDarkMode";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../../../pages/staff/dropdownStyles";

/** Accept Date, yyyy-mm-dd string, or null/undefined from parent state. */
function parseInitialDate(val) {
  if (val == null || val === "") return null;
  if (val instanceof Date && !isNaN(val.getTime())) return val;
  if (typeof val === "string") {
    const match = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, y, m, d] = match;
      const dt = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
      return isNaN(dt.getTime()) ? null : dt;
    }
    const dt = new Date(val);
    return isNaN(dt.getTime()) ? null : dt;
  }
  return null;
}

const ThreeDropdownBirthdatePicker = ({ onDateChange, initialDate = null }) => {
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const lastEmittedRef = useRef(null);

  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const months = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(0, i).toLocaleString("default", { month: "long" }),
  }));

  const getMonthName = (monthNumber) => {
    if (!monthNumber) return "";
    return new Date(0, monthNumber - 1).toLocaleString("default", { month: "long" });
  };

  // Year is optional for listing days: use a leap year so Feb can show 1–29 until a real year is chosen.
  const generateDays = (m, y) => {
    if (!m) return [];
    const yearForDays = y || 2000;
    const daysInMonth = new Date(yearForDays, m, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  };

  const yearOptions = Array.from({ length: 121 }, (_, i) => new Date().getFullYear() - i).map(
    (y) => ({ label: String(y), value: y })
  );

  // Sync prop into local state before paint so emit effect never sees a stale empty triplet on load.
  useLayoutEffect(() => {
    const parsed = parseInitialDate(initialDate);
    if (parsed) {
      setYear(parsed.getFullYear());
      setMonth(parsed.getMonth() + 1);
      setDay(parsed.getDate());
    } else {
      setYear("");
      setMonth("");
      setDay("");
    }
  }, [initialDate]);

  // If month/year change, clear day when it is invalid for that month (e.g. 31 → Feb)
  useEffect(() => {
    if (!day || !month || !year) return;
    const maxDay = new Date(year, month, 0).getDate();
    if (day > maxDay) setDay("");
  }, [month, year, day]);

  // Notify parent: full date, or null when any part is missing / cleared
  useEffect(() => {
    if (!onDateChange) return;

    if (day && month && year) {
      const birthDate = new Date(year, month - 1, day);
      if (isNaN(birthDate.getTime())) return;

      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDifference = today.getMonth() - birthDate.getMonth();
      if (
        monthDifference < 0 ||
        (monthDifference === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }

      const y = birthDate.getFullYear();
      const m = String(birthDate.getMonth() + 1).padStart(2, "0");
      const d = String(birthDate.getDate()).padStart(2, "0");
      const formattedDate = `${y}-${m}-${d}`;

      if (lastEmittedRef.current !== formattedDate) {
        lastEmittedRef.current = formattedDate;
        onDateChange({ date: birthDate, age, formattedDate });
      }
    } else {
      if (lastEmittedRef.current !== null) {
        lastEmittedRef.current = null;
        onDateChange({ formattedDate: null, date: null, age: null });
      }
    }
  }, [day, month, year, onDateChange]);

  const dayOptions = generateDays(month, year).map((d) => ({
    label: String(d),
    value: d,
  }));

  return (
    <div className="three-dropdown-birthdate">
      <div className="row">
        <div className="col-4">
          <Select
            isClearable
            value={month ? { label: getMonthName(month), value: month } : null}
            onChange={(option) => {
              if (option == null) {
                setMonth("");
                setDay("");
              } else {
                setMonth(option.value);
              }
            }}
            options={months}
            styles={dropdownStyles}
            placeholder="Month"
          />
        </div>
        <div className="col-4">
          <Select
            isClearable
            value={day !== "" && day != null ? { label: String(day), value: day } : null}
            onChange={(option) => {
              if (option == null) setDay("");
              else setDay(option.value);
            }}
            options={dayOptions}
            styles={dropdownStyles}
            placeholder="Day"
            isDisabled={!month}
            noOptionsMessage={() => (month ? "No days" : "Select month first")}
          />
        </div>
        <div className="col-4">
          <Select
            isClearable
            value={year !== "" && year != null ? { label: String(year), value: year } : null}
            onChange={(option) => {
              if (option == null) {
                setYear("");
                setDay("");
              } else {
                setYear(option.value);
              }
            }}
            options={yearOptions}
            styles={dropdownStyles}
            placeholder="Year"
          />
        </div>
      </div>
    </div>
  );
};

export { ThreeDropdownBirthdatePicker };
